/**
 * Mission Tracker work-graph contracts (APP-4, ADR 0082 / 0083).
 *
 * WHY: Mission Tracker models work as a **directed graph** — a `WorkNode` is a typed unit of
 * work, a `WorkEdge` a typed relation — so a pure engine (`@squadlab/work-graph`) can compute
 * the critical path and a Monte-Carlo forecast over it. These shapes are the graph's wire
 * form: projected-from-issues and persisted-on-read is external input, so it is PARSED
 * (ADR 0005) against these schemas.
 *
 * PARITY vs NATIVE (ADR 0083): every node splits into a `parity` facet (mirrors the
 * Linear/tracker issue — the ONLY surface the sync engine touches) and a `native` facet
 * (ours, Linear-blind). Retiring Linear removes the parity sync; the native model and the
 * engine on top of it never move.
 */
import { z } from 'zod';
import { PrioritySchema, StatusCategorySchema } from './domain.js';

/** Non-empty identifier (ULID in practice). */
const idSchema = z.string().min(1);

/** What kind of work a node is — drives distribution family, routing, and how it's checked. */
export const WORK_NODE_TYPES = ['build', 'ops', 'decision', 'review', 'purchase'] as const;
export const WorkNodeTypeSchema = z.enum(WORK_NODE_TYPES);
export type WorkNodeType = z.infer<typeof WorkNodeTypeSchema>;

/** Routine work has tight, predictable effort; novel work is wide and spawns discovery. */
export const WORK_NOVELTIES = ['routine', 'novel'] as const;
export const WorkNoveltySchema = z.enum(WORK_NOVELTIES);
export type WorkNovelty = z.infer<typeof WorkNoveltySchema>;

/** Who can sign a node off — an agent can check some work; other work needs a human. */
export const WORK_VERIFICATIONS = ['agent', 'human'] as const;
export const WorkVerificationSchema = z.enum(WORK_VERIFICATIONS);
export type WorkVerification = z.infer<typeof WorkVerificationSchema>;

/**
 * A three-point effort estimate in minutes (PERT / triangular): optimistic ≤ likely ≤
 * pessimistic. Uncertainty is first-class — the deterministic critical path uses a point
 * summary (see `@squadlab/work-graph`), and the Monte-Carlo forecast samples the whole range.
 * Fitted per-type / per-person distributions replace the priors once calibration data exists
 * (Phase 5); the *shape* stays a range either way.
 */
export const EffortEstimateSchema = z
  .object({
    optimisticMin: z.number().positive(),
    likelyMin: z.number().positive(),
    pessimisticMin: z.number().positive(),
  })
  .refine((e) => e.optimisticMin <= e.likelyMin && e.likelyMin <= e.pessimisticMin, {
    message: 'effort estimate must satisfy optimistic ≤ likely ≤ pessimistic',
  });
export type EffortEstimate = z.infer<typeof EffortEstimateSchema>;

/**
 * The PARITY facet — mirrors the Linear / tracker issue. The sync engine reads and writes
 * ONLY these fields (ADR 0083); none of it is ours to own until a per-field system-of-record
 * flip. (`title` is a parity-KIND field regardless of whether this particular node is linked.)
 */
export const WorkNodeParitySchema = z.object({
  title: z.string().min(1),
  statusCategory: StatusCategorySchema,
  assigneeId: idSchema.nullable(),
  priority: PrioritySchema,
});
export type WorkNodeParity = z.infer<typeof WorkNodeParitySchema>;

/**
 * The NATIVE facet — ours, invisible to Linear. Edges live on the graph (not here); this is
 * per-node native state: how the work behaves (type / novelty / verification / effort) and
 * where attention sits — `ownerId` = accountable (DRI), `holderId` = whose court the ball is
 * in (the locus of attention, which status transitions route without changing the owner).
 */
export const WorkNodeNativeSchema = z.object({
  type: WorkNodeTypeSchema,
  novelty: WorkNoveltySchema,
  verification: WorkVerificationSchema,
  effort: EffortEstimateSchema,
  ownerId: idSchema.nullable(),
  holderId: idSchema.nullable(),
});
export type WorkNodeNative = z.infer<typeof WorkNodeNativeSchema>;

export const WorkNodeSchema = z.object({
  id: idSchema,
  /** Provenance: the tracker issue this projected from; `null` = a natively-born node. */
  sourceIssueId: idSchema.nullable(),
  parity: WorkNodeParitySchema,
  native: WorkNodeNativeSchema,
});
export type WorkNode = z.infer<typeof WorkNodeSchema>;

/**
 * Edge kinds. `depends-on` and `waits-on` are PRECEDENCE constraints; `feeds-back` is a
 * rework loop, kept as an annotation and excluded from the acyclic critical-path / forecast
 * pass so a review→owner loop can't make the schedule graph cyclic.
 */
export const WORK_EDGE_KINDS = ['depends-on', 'waits-on', 'feeds-back'] as const;
export const WorkEdgeKindSchema = z.enum(WORK_EDGE_KINDS);
export type WorkEdgeKind = z.infer<typeof WorkEdgeKindSchema>;

/** The precedence edge kinds — the ones that constrain scheduling (i.e. not `feeds-back`). */
export const PRECEDENCE_EDGE_KINDS = ['depends-on', 'waits-on'] as const satisfies readonly WorkEdgeKind[];

/** True when this edge kind imposes a precedence constraint (`fromId` before `toId`). */
export function isPrecedenceEdge(kind: WorkEdgeKind): boolean {
  return (PRECEDENCE_EDGE_KINDS as readonly WorkEdgeKind[]).includes(kind);
}

/**
 * A directed edge in PRECEDENCE direction: `fromId` completes before `toId` starts (for
 * `depends-on` / `waits-on`). So "issue X is blocked-by Y" projects to
 * `{ fromId: Y, toId: X, kind: 'depends-on' }` — Y (the blocker) precedes X. `feeds-back`
 * points along the rework direction and does not constrain the acyclic pass.
 */
export const WorkEdgeSchema = z.object({
  id: idSchema,
  kind: WorkEdgeKindSchema,
  fromId: idSchema,
  toId: idSchema,
});
export type WorkEdge = z.infer<typeof WorkEdgeSchema>;

/** The whole work graph — the unit the engine analyses and the projection produces. */
export const WorkGraphSchema = z.object({
  /** Snapshot schema version — bump alongside breaking shape changes. */
  version: z.literal(1),
  nodes: z.array(WorkNodeSchema),
  edges: z.array(WorkEdgeSchema),
});
export type WorkGraph = z.infer<typeof WorkGraphSchema>;
